import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import LoadingSpinner from "../Components/LoadingSpinner";
import _ from "lodash";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import Page from "../Components/Page";
import gql from "graphql-tag";
import {getStringParamOrDefault, updateQueryStringForState} from "../../utils/urlBuilder";
import ReactTable from "../Components/ReactTable";
import BaseOutlierInputBlur from "./BaseOutlierInputBlur";
import RetailerIdAndName from "../Components/RetailerIdAndName";
import DateRangeInput from "../Components/DateRangePicker";
import {Breadcrumbs} from "../Components/Breadcrumbs";

function BaseOutlierCountAlert({accessor, alertType, threshold = 25}) {

    const countAlertColumns = [
        {
            header: "Retailer",
            accessorKey: "retailerId",
            cell: ({row}) => <RetailerIdAndName retailerId={row.original.retailerId}/>,
            className: "retail-cell-element ops-alert-element",
            size: 60,
            enableColumnFilter: false
        },
        {
            header: "Store Id",
            accessorKey: "storeId",
            className: "store-cell-element ops-alert-element",
            size: 60
        },
        {
            header: "Sales End Date",
            accessorKey: "weekEndDate",
            className: "week-end-date-cell-element ops-alert-element",
            size: 60
        },
        {
            header: `${alertType} Count`,
            accessorKey: `${accessor}Count`,
            cell: props => <div
                className="record-count-cell-element ops-alert-element">{_.round(props.getValue(), 2)}</div>
        },
        {
            header: `Average ${alertType} Count`,
            accessorKey: `average${alertType}Count`,
            enableColumnFilter: false,
            cell: props => <div
                className="record-count-cell-element ops-alert-element">{_.round(props.getValue(), 2)}</div>
        },
        {
            header: `Percent Difference From Average ${alertType} Count`,
            accessorKey: `diffFromAverage${alertType}`,
            enableColumnFilter: false,
            cell: props => <div
                className="record-count-cell-element ops-alert-element">{_.round(props.getValue(), 2)}</div>
        }
    ];

    const [alertData, setAlertData] = useState(undefined,)
    const [loading, setLoading] = useState(false)
    const [retailerId, setRetailerId] = useState(getStringParamOrDefault("retailerId", undefined))
    const [percentThreshold, setPercentThreshold] = useState(getStringParamOrDefault("percentThreshold", threshold))
    const [startDate, setStartDate] = useState(getStringParamOrDefault("startDate", undefined))
    const [endDate, setEndDate] = useState(getStringParamOrDefault("endDate", undefined))

    const urlParameters = {
        retailerId,
        percentThreshold,
        startDate,
        endDate
    }
    updateQueryStringForState(urlParameters)
    const handleDateChange = ({fromFormatted, toFormatted}) => {
        setStartDate(fromFormatted || null);
        setEndDate(toFormatted || null);
        updateQueryStringForState({startDate: fromFormatted, endDate: toFormatted});
    };

    function retrieveAlertData() {
        if (!retailerId) {
            return;
        }
        setLoading(true);
        setAlertData(undefined);

        // noinspection GraphQLUnresolvedReference
        makeApolloClientCall(
            gql`query storeSalesDataOutliers($retailerId: [Int!],
                $percentDifferenceThreshold: Int!,
                $percentDifferenceMeasure: differenceMeasureType!,
                $startDate: String, $endDate: String)
            {
                storeSalesDataOutliers(
                    retailerId: $retailerId,
                    percentDifferenceThreshold: $percentDifferenceThreshold,
                    percentDifferenceMeasure: $percentDifferenceMeasure,
                    startDate: $startDate,
                    endDate: $endDate
                )
                {
                    data {
                        retailerId,
                        storeId,
                        weekEndDate,
                        recordCount,
                        averageRecordCount,
                        diffFromAverageRecord,
                        unitCount,
                        averageUnitCount,
                        diffFromAverageUnit,
                        dollarCount,
                        averageDollarCount,
                        diffFromAverageDollar
                    }
                }
            }`, false, {
                retailerId: parseInt(retailerId),
                percentDifferenceThreshold: parseInt(percentThreshold),
                percentDifferenceMeasure: accessor,
                startDate: startDate,
                endDate: endDate
            })
            .then((alertData) => {
                setLoading(false);
                setAlertData(alertData);
            })
            .catch((error) => {
                setLoading(false);
                console.error(error);
            });
    }

    useEffect(() => {
        if (retailerId) {
            retrieveAlertData();
        }
    }, []);

    return (
        <Page name={`${alertType} Counts By Store`}
              breadCrumbs={<Breadcrumbs items={[{label: "Alerts"}, {label: "Outliers"}, {label: alertType}]}/>}
        >
            <div className={"base-outlier-count-alert-container"}>
                <div className={"base-outlier-count-alert-column"}>
                    <div className={"base-outlier-count-alert-row"}>
                        <BaseOutlierInputBlur labelText={"Retailer Id:"}
                                              defaultValue={retailerId}
                                              onBlur={(event) => setRetailerId(event.target.value)}/>
                        <BaseOutlierInputBlur labelText={"Threshold %:"}
                                              defaultValue={percentThreshold}
                                              onBlur={(event) => setPercentThreshold(event.target.value)}/>
                        <div className="date-range-group">
                            <DateRangeInput
                                data-testid="date-range-input-wrapper"
                                startDate={startDate}
                                endDate={endDate}
                                onChange={handleDateChange}
                                numberOfMonths={2}
                                allowManualInput={true}
                                showMonthYearDropdown={false}
                            />
                        </div>
                    </div>
                </div>
                <div className={"base-outlier-count-alert-column"}>
                    <div className={"base-outlier-count-alert-row"}>
                        <input type="submit" className="button" value="Submit" onClick={retrieveAlertData}/>
                    </div>
                </div>
            </div>

            {loading && <LoadingSpinner/>}
            {alertData && <ReactTable
                filterable
                data={alertData.storeSalesDataOutliers.data}
                columns={countAlertColumns}
                initialState={{sorting: [{id: `diffFromAverage${alertType}`, desc: true}]}}
            />}
        </Page>
    );
}

BaseOutlierCountAlert.propTypes = {
    accessor: PropTypes.string,
    alertType: PropTypes.string,
    threshold: PropTypes.number
};

export default BaseOutlierCountAlert;